import { getTokenStatus, shouldPushToken } from '../../utils/adaToken';

const ADA_READY_EVENT = 'ada:ready';
// Non-sensitive diagnostic meta fields: they tell the Ada team which front opened the chat and
// whether the token was sent. Never put the token, its expiry or a member id here.
const ADA_FRONT = 'legacy';

// Ada is injected by GTM, possibly late (e.g. once the cookie consent is given), so the bridge
// keeps looking for it for about 10 minutes: every second during the first 30 seconds (normal
// page load), then every 10 seconds. That is at most 87 cheap property reads per page.
const DEFAULT_POLL_PHASES = [
    { intervalMs: 1000, attempts: 30 },
    { intervalMs: 10000, attempts: 57 }
];

const defaultTimers = {
    setInterval: (callback, delay) => setInterval(callback, delay),
    clearInterval: (id) => clearInterval(id)
};

/**
 * Sends the member access token to the Ada chat widget.
 *
 * Ada is loaded by GTM, so it may start before or after this bundle, and GTM may replace
 * window.adaSettings. The bridge therefore does not rely on Ada settings callbacks:
 * - it still writes the token in window.adaSettings.sensitiveMetaFields (read by Ada if it starts later);
 * - as soon as window.adaEmbed exists, it sends the token with setSensitiveMetaFields() and again
 *   on the 'ada:ready' event (checked in production: this updates the variable used by Ada actions,
 *   whether the chat is open or closed, even in an ongoing conversation);
 * - it sends every new token the Redux store receives.
 * It also sends the he_front and he_token_status meta fields the same way (adaSettings.metaFields,
 * then setMetaFields()), on a separate channel so that one failing never blocks the other.
 */
export const startAdaTokenBridge = ({
    store,
    win = window,
    now = () => Date.now(),
    timers = defaultTimers,
    pollPhases = DEFAULT_POLL_PHASES
}) => {
    let pushedToken = null;
    let pushedStatus = null;
    let attachedEmbed = null;

    // Ada is disabled on some pages (messaging v2) with `window.adaSettings = null`.
    const isAdaDisabled = () => win.adaSettings === null;

    const getEmbed = () => {
        const embed = win.adaEmbed;
        return embed && typeof embed.setSensitiveMetaFields === 'function' ? embed : null;
    };

    const getStoreAuth = () => store.getState().auth || {};

    const sendToEmbed = (token) => {
        const embed = getEmbed();
        if (!embed) {
            return;
        }
        Promise.resolve()
            .then(() => embed.setSensitiveMetaFields({ access_token: token }))
            .catch((e) => {
                console.warn('[Ada] setSensitiveMetaFields failed', e);
                if (pushedToken === token) {
                    pushedToken = null;
                }
            });
    };

    const push = (token) => {
        pushedToken = token;
        const settings = win.adaSettings || {};
        win.adaSettings = {
            ...settings,
            sensitiveMetaFields: { ...settings.sensitiveMetaFields, access_token: token }
        };
        sendToEmbed(token);
    };

    const sendMetaFieldsToEmbed = (metaFields) => {
        const embed = getEmbed();
        if (!embed || typeof embed.setMetaFields !== 'function') {
            return;
        }
        Promise.resolve()
            .then(() => embed.setMetaFields(metaFields))
            .catch((e) => {
                console.warn('[Ada] setMetaFields failed', e);
                if (pushedStatus === metaFields.he_token_status) {
                    pushedStatus = null;
                }
            });
    };

    const pushMetaFields = (status) => {
        pushedStatus = status;
        const metaFields = { he_front: ADA_FRONT, he_token_status: status };
        const settings = win.adaSettings || {};
        // Merge: GTM or Ada may already have set other meta fields.
        win.adaSettings = {
            ...settings,
            metaFields: { ...settings.metaFields, ...metaFields }
        };
        sendMetaFieldsToEmbed(metaFields);
    };

    const sync = ({ force = false } = {}) => {
        if (isAdaDisabled()) {
            return;
        }
        const auth = getStoreAuth();
        const nowMs = now();
        if (
            shouldPushToken({
                token: auth.accessToken,
                expiresAt: auth.expiresAt,
                pushedToken,
                force,
                nowMs
            })
        ) {
            push(auth.accessToken);
        }
        const status = getTokenStatus({ token: auth.accessToken, expiresAt: auth.expiresAt, nowMs });
        if (force || status !== pushedStatus) {
            pushMetaFields(status);
        }
    };

    const attachEmbed = (embed) => {
        attachedEmbed = embed;
        try {
            if (typeof embed.subscribeEvent === 'function') {
                Promise.resolve(embed.subscribeEvent(ADA_READY_EVENT, () => sync({ force: true }))).catch(
                    (e) => console.warn('[Ada] subscribeEvent failed', e)
                );
            }
        } catch (e) {
            console.warn('[Ada] subscribeEvent failed', e);
        }
        // Ada may have started without the token (it was loaded before this bundle): send it now.
        sync({ force: true });
    };

    // Runs the poll phases one after the other until Ada is found, the page opts out, or all
    // phases are exhausted.
    const pollForEmbed = (phaseIndex = 0) => {
        const phase = pollPhases[phaseIndex];
        if (!phase) {
            return;
        }
        let attempts = 0;
        const pollId = timers.setInterval(() => {
            attempts += 1;
            const found = getEmbed();
            if (found || isAdaDisabled()) {
                timers.clearInterval(pollId);
                if (found && !isAdaDisabled() && found !== attachedEmbed) {
                    attachEmbed(found);
                }
                return;
            }
            if (attempts >= phase.attempts) {
                timers.clearInterval(pollId);
                pollForEmbed(phaseIndex + 1);
            }
        }, phase.intervalMs);
    };

    if (isAdaDisabled()) {
        return;
    }

    store.subscribe(() => sync());
    const embed = getEmbed();
    if (embed) {
        // attachEmbed() runs the first sync itself, so the token is sent once.
        attachEmbed(embed);
    } else {
        sync();
        pollForEmbed();
    }
};
